import { CreditCard } from '@accedo/one-plugin-sdk';
import { t } from 'i18next';
import styled, { css } from 'styled-components';

import { FormInputTypes, TFormData } from '#/client';
import { InputField } from '#/components/Form/Form.types';
import { MEDIA_QUERIES } from '#/hooks/useResponsiveValue';
import { BreakpointKeys, FormInputVariants } from '#/service/constants';
import { remify } from '#/service/utils';

import CvvHelp from './CvvHelp';

interface PlanPickerSubTextProps {
  $isCentered?: boolean;
}

export const cssPlanContainerBase = css`
  border-radius: ${remify(8)};
  padding: ${remify(24)};
  background-color: var(--pageSecondaryBackground);
  color: var(--pageParagraphForeground);
`;

export const PlanPickerGrid = styled.div`
  display: grid;
  grid-template-columns: auto max-content;
  column-gap: ${remify(32)};
  align-items: center;

  @media screen and ${MEDIA_QUERIES[BreakpointKeys.XS]} {
    grid-template-columns: 1fr;
  }
`;

PlanPickerGrid.displayName = 'PlanPickerGrid';

export const PlanPickerMainText = styled.div.attrs(() => ({
  'data-testid': 'plan-picker-main-text',
}))`
  font: var(--fontStyleBody);
`;

PlanPickerMainText.displayName = 'PlanPickerMainText';

export const PlanPickerSubText = styled.div.attrs(() => ({
  'data-testid': 'plan-picker-sub-text',
}))<PlanPickerSubTextProps>`
  opacity: 0.7;
  font: var(--fontStyleFootnote);
  ${({ $isCentered }) => $isCentered && `text-align: center;`}
`;

PlanPickerSubText.displayName = 'PlanPickerSubText';

export enum PaymentFormKey {
  CardHolderName = 'cardHolderName',
  CardNumber = 'cardNumber',
  ExpirationDate = 'expirationDate',
  SecurityCode = 'securityCode',
  BillingZipCode = 'billingZipCode',
}

export const getPaymentInputFields = (
  isCardRequired: boolean
): InputField[] => {
  return [
    {
      key: PaymentFormKey.CardHolderName,
      type: FormInputTypes.INPUT,
      variant: FormInputVariants.TEXT,
      label: t('iap.inputField.cardHolderName.label'),
      required: isCardRequired,
    },
    {
      key: PaymentFormKey.CardNumber,
      type: FormInputTypes.INPUT,
      variant: FormInputVariants.NUMBER,
      label: t('iap.inputField.cardNumber.label'),
      required: isCardRequired,
      format: '#### #### #### ####',
    },
    {
      key: PaymentFormKey.ExpirationDate,
      type: FormInputTypes.INPUT,
      variant: FormInputVariants.NUMBER,
      label: t('iap.inputField.expirationDate.label'),
      required: isCardRequired,
      format: '##/##',
    },
    {
      key: PaymentFormKey.SecurityCode,
      type: FormInputTypes.INPUT,
      variant: FormInputVariants.TEXT,
      label: t('iap.inputField.securityCode.label'),
      required: isCardRequired,
      help: <CvvHelp>{t('iap.inputField.securityCode.help')}</CvvHelp>,
    },
    {
      key: PaymentFormKey.BillingZipCode,
      type: FormInputTypes.INPUT,
      variant: FormInputVariants.TEXT,
      label: t('iap.inputField.billingZipCode.label'),
      required: isCardRequired,
    },
  ];
};

export const getVoucherInputField = (): InputField => {
  return {
    key: 'voucher',
    type: FormInputTypes.INPUT,
    variant: FormInputVariants.TEXT,
    label: t('iap.inputField.voucher.label'),
    required: false,
  };
};

export const formDataToCreditCard = (
  formData: TFormData
): CreditCard | undefined => {
  const cardHolderName = formData[PaymentFormKey.CardHolderName] as string;
  const cardNumber = (formData[PaymentFormKey.CardNumber] as string) ?? '';
  const expiryDate = formData[PaymentFormKey.ExpirationDate] as string;
  const cvv = formData[PaymentFormKey.SecurityCode] as string;
  const billingZip = formData[PaymentFormKey.BillingZipCode] as string;

  // if all fields are empty, that means we're trying to subscribe without
  // providing credit card details which can be allowed by some vouchers.
  const isEmpty = ![
    cardHolderName,
    cardNumber,
    expiryDate,
    cvv,
    billingZip,
  ].some(Boolean);

  if (isEmpty) {
    return undefined;
  }

  const expirationSegments = expiryDate?.split('/') || [0, 0];
  const expirationMonth = +expirationSegments[0];
  const expirationYear = +expirationSegments[1];

  return {
    cardHolderName,
    cardNumber: cardNumber.replace(/\s/g, ''),
    expiryDate,
    expirationMonth,
    expirationYear,
    cvv,
    billingZip,
  };
};
